import type React from "react"
import { useEffect, useRef, useState } from "react"
import { plural, t } from "@lingui/core/macro"
import { isMobileDevice } from "@multimediallc/web-utils/modernizr"
import { isAndroid, isIOS } from "react-device-detect"
import { useAppContext } from "../../../hooks/appContext"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { useAppDispatch, useAppSelector } from "../../../store/hooks"
import { useGetProfileQuery } from "../../../store/messagingSlice"
import { updateTokenBalance } from "../../../store/userSlice"
import { fetchPost } from "../../../utils/myFetch"
import { addPageAction } from "../../../utils/newrelic"
import { OneClickFlowNoLink } from "../../chat/OneClickFlow"
import Button, { ButtonSize } from "../../common/atoms/Button"
import { Tokens } from "../../common/atoms/Icons/Chat"
import { BlackClose, Divider } from "../../common/atoms/Icons/Others"
import { Overlay } from "../../common/atoms/Overlay"
import { Textarea } from "../../common/atoms/Textarea/Textarea"
import { TextInput } from "../../common/atoms/TextInput/TextInput"
import { Typography } from "../../common/atoms/Typography"
import { ConfirmModal } from "../../common/molecules/ConfirmModal/ConfirmModal"
import { DM_WINDOW_LOCATION, INBOX_VERSION_V2 } from "../constants"
import { getLastTipAmount, setLastTipAmount } from "../tipAmountCache"
import { RoomType, TipType } from "../types"
import { classNames } from "../utils"
import styles from "./SendTip.module.scss"

const SEND_TIP_API_URL = (username: string) => `/tipping/send_tip/${username}/`
const PURCHASE_TOKENS_URL = "/tipping/purchase_tokens/?source=send_tip_dialog"
const MAX_MESSAGE_LENGTH = 255
const MAX_TIP_DIGITS = 16
const DEFAULT_TIP_AMOUNT = "25"
const ZERO_TIP_AMOUNT = "0"

enum CloseType {
    Normal = "normal",
    WithTipSent = "withTipSent",
}

interface SendTipResponse {
    success: boolean
    token_balance?: number
    error?: string
}

interface SendTipProps {
    username: string
    onClose: (closeType?: CloseType) => void
    initialTipAmount?: string
    trackNewConversationMessageSent: () => void
}

const KEYBOARD_UPDATE_DELAY_MS = isIOS ? 150 : 350

export const SendTip: React.FC<SendTipProps> = ({
    username,
    onClose,
    initialTipAmount,
    trackNewConversationMessageSent,
}) => {
    const { context } = useAppContext()
    const dispatch = useAppDispatch()
    const tokenBalance = useAppSelector(
        (state) => state.user.loggedInUser?.token_balance ?? 0,
    )
    const [tipAmount, setTipAmount] = useState<string>(() => {
        return (
            initialTipAmount || getLastTipAmount(username) || DEFAULT_TIP_AMOUNT
        )
    })
    const isDesktop = !isMobileDevice()
    const { data: profile } = useGetProfileQuery(username || "")
    const [message, setMessage] = useState<string>("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [show, setShow] = useState(false)
    const [showOneClickFlow, setShowOneClickFlow] = useState(false)

    const tipInputRef = useRef<HTMLInputElement>(null)
    const messageInputRef = useRef<HTMLTextAreaElement>(null)

    const isTipAmountValid = (amount: string) => {
        if (amount === "" || amount === ZERO_TIP_AMOUNT) return false
        const numericAmount = Number(amount)
        return !isNaN(numericAmount) && numericAmount > 0
    }

    const isOneClickEligible = context.logged_in_user?.can_weg_oneclick

    useEffect(() => {
        const timer = setTimeout(() => {
            setShow(true)
        }, KEYBOARD_UPDATE_DELAY_MS)
        return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
        if (
            document.activeElement === tipInputRef.current ||
            document.activeElement === messageInputRef.current
        ) {
            tipInputRef.current?.blur()
            messageInputRef.current?.blur()
        }
    }, [document.activeElement])

    const handleTipAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleanValue = e.target.value
            .replace(/[^\d]+/g, "")
            .substring(0, MAX_TIP_DIGITS)

        const formattedValue = cleanValue.replace(/^0+/, "") || "0"
        setTipAmount(cleanValue === "" ? "" : formattedValue)
    }

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value)
        if (!isDesktop) {
            const textarea = e.target
            textarea.style.height = `${textarea.scrollHeight}px`
        }
    }

    const handleClose = (closeType?: CloseType) => {
        addPageAction("DmTipMenu", {
            actionType:
                closeType === CloseType.WithTipSent
                    ? "closeWithTipSent"
                    : "close",
            canTip: profile?.can_tip,
        })
        onClose(closeType)
    }

    const handleSubmit = async () => {
        if (isSubmitting) return

        if (!isTipAmountValid(tipAmount)) return
        const numericTipAmount = Number(tipAmount)

        addPageAction("SendTipClicked", {
            amount: tipAmount,
            location: DM_WINDOW_LOCATION,
            hasTipMessage: message.length > 0,
            inboxVersion: INBOX_VERSION_V2,
        })

        if (numericTipAmount > tokenBalance) {
            setError(t`You do not have enough tokens.`)
            return
        }

        setIsSubmitting(true)
        try {
            const response = await fetchPost(SEND_TIP_API_URL(username), {
                tip_amount: tipAmount,
                message,
                source: "DM",
                tip_room_type: RoomType.DM,
                tip_type: TipType.public,
                video_mode: "",
                from_username: context.logged_in_user?.username || "",
            })

            if (response.error) {
                setError(response.error)
                return
            }

            const data = response.jsonData as SendTipResponse
            if (data.success) {
                if (data.token_balance !== undefined) {
                    dispatch(updateTokenBalance(data.token_balance))
                }
                setLastTipAmount(username, tipAmount)
                addPageAction("SendTipSuccess", {
                    amount: tipAmount,
                    location: DM_WINDOW_LOCATION,
                    hasTipMessage: message.length > 0,
                    inboxVersion: INBOX_VERSION_V2,
                })

                trackNewConversationMessageSent()

                handleClose(CloseType.WithTipSent)
            } else if (data.error) {
                setError(data.error)
            }
        } catch (e) {
            setError(t`Failed to send tip. Please try again.`)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handlePurchaseTokens = () => {
        addPageAction("DmTipMenu", {
            actionType: "addTokensClicked",
            canTip: profile?.can_tip,
        })
        if (isOneClickEligible) {
            setShowOneClickFlow(true)
        } else {
            window.open(
                PURCHASE_TOKENS_URL,
                "_blank",
                "height=615,width=850,scrollbars=1",
            )
        }
        setError(null)
    }

    const closeOneClickFlow = () => {
        setShowOneClickFlow(false)
    }

    const openFullPurchasePage = () => {
        window.open(
            PURCHASE_TOKENS_URL,
            "_blank",
            "height=615,width=850,scrollbars=1",
        )
        closeOneClickFlow()
    }

    const isInsufficientTokens = error === t`You do not have enough tokens.`

    return (
        <>
            <Overlay
                onClick={() => handleClose(CloseType.Normal)}
                data-testid="dark-overlay"
                className={classNames(styles.darkOverlay, {
                    [styles.desktop]: isDesktop,
                })}
            />
            <div
                className={classNames(styles.overlay, {
                    [styles.android]: isAndroid,
                    [styles.show]: show,
                })}
            >
                <div
                    className={styles.sendTipContainer}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        className={classNames(styles.header, {
                            [styles.desktop]: isDesktop,
                        })}
                    >
                        <Typography
                            weight={Weights.medium}
                            className={classNames(styles.sendTipTitle, {
                                [styles.desktop]: isDesktop,
                            })}
                        >
                            {t`Send Tip to`} {username}
                        </Typography>
                        <div className={styles.headerRightContent}>
                            <Button
                                icon={<BlackClose height={24} width={24} />}
                                onClick={() => handleClose(CloseType.Normal)}
                                className={classNames(styles.closeButton, {
                                    [styles.desktop]: isDesktop,
                                })}
                            />
                        </div>
                    </div>

                    <div
                        className={classNames(styles.contentWrapper, {
                            [styles.desktop]: isDesktop,
                        })}
                    >
                        <div className={styles.tipAmountRow}>
                            <div className={styles.tipAmountLabelRow}>
                                <Typography
                                    className={styles.label}
                                    id="tip-amount-label"
                                    size={isDesktop ? Sizes.xspx : Sizes.mdpx}
                                >
                                    {t`Enter tip amount`}
                                </Typography>
                            </div>
                            <div className={styles.tipAmountWrapper}>
                                <TextInput
                                    type="tel"
                                    value={tipAmount}
                                    onChange={handleTipAmountChange}
                                    className={classNames(
                                        styles.tipAmountInput,
                                        { [styles.desktop]: isDesktop },
                                    )}
                                    data-testid="tip-amount-input"
                                    autoFocus={isAndroid}
                                    disabled={isSubmitting}
                                />
                                <Typography
                                    className={classNames(styles.tokensLabel, {
                                        [styles.desktop]: isDesktop,
                                    })}
                                >
                                    {plural(Number(tipAmount), {
                                        one: "Token",
                                        other: "Tokens",
                                    })}
                                </Typography>
                            </div>
                        </div>

                        <div className={styles.messageRow}>
                            <label className={styles.label}>
                                <Textarea
                                    value={message}
                                    onChange={handleMessageChange}
                                    className={classNames(
                                        styles.optionalMessage,
                                        { [styles.desktop]: isDesktop },
                                    )}
                                    placeholder={t`Optional message`}
                                    maxLength={MAX_MESSAGE_LENGTH}
                                    disabled={isSubmitting}
                                />
                            </label>
                        </div>
                        <div className={styles.sendRow}>
                            <div className={styles.tokens}>
                                <Tokens
                                    height={isDesktop ? 12 : 16}
                                    width={isDesktop ? 12 : 16}
                                    className={styles.tokensBlue}
                                />
                                <Typography
                                    className={styles.tokensAmount}
                                    size={isDesktop ? Sizes.xspx : Sizes.smpx}
                                    weight={Weights.medium}
                                >
                                    {tokenBalance}
                                </Typography>
                                <Divider height={8} width={8} />
                                <Button
                                    text={t`Add Tokens`}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handlePurchaseTokens()
                                    }}
                                    className={classNames(styles.buyMore, {
                                        [styles.desktop]: isDesktop,
                                    })}
                                />
                            </div>
                            <Button
                                text={t`Send`}
                                onClick={handleSubmit}
                                disabled={
                                    !isTipAmountValid(tipAmount) || isSubmitting
                                }
                                className={classNames(styles.sendButton, {
                                    [styles.desktop]: isDesktop,
                                })}
                                size={ButtonSize.Medium}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.errorModal}>
                <ConfirmModal
                    isOpen={error !== null}
                    onClose={() => setError(null)}
                    onConfirm={
                        isInsufficientTokens
                            ? handlePurchaseTokens
                            : () => setError(null)
                    }
                    message={error || ""}
                    confirmText={
                        isInsufficientTokens ? t`Purchase Tokens` : t`OK`
                    }
                    cancelText={isInsufficientTokens ? t`Cancel` : undefined}
                    useOldModalLayout={isDesktop}
                />
            </div>
            {showOneClickFlow && (
                <>
                    <Overlay
                        onClick={closeOneClickFlow}
                        className={styles.oneClickFlowOverlay}
                    />
                    <div
                        className={styles.oneClickFlowBottomSheet}
                        data-testid="oneclick-flow-container"
                    >
                        <button
                            className={styles.oneClickCloseButton}
                            onClick={closeOneClickFlow}
                        >
                            <div
                                className={styles.closeButtonSlash}
                                style={{ transform: "rotate(-45deg)" }}
                            />
                            <div
                                className={styles.closeButtonSlash}
                                style={{ transform: "rotate(45deg)" }}
                            />
                        </button>
                        <div className={styles.oneClickFlowWrapper}>
                            <OneClickFlowNoLink
                                source="send_tip_dialog"
                                desktop={false}
                                tokenBalance={tokenBalance}
                                openPurchasePage={openFullPurchasePage}
                                closeModal={closeOneClickFlow}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    )
}
